import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { AuthContext } from "../AuthContext";
import "./AdminUpload.css";

function AdminUpload() {
  const navigate = useNavigate();
  const { role, token } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [sizeInput, setSizeInput] = useState("");
  const [sizePlaceholder, setSizePlaceholder] = useState("800x600");
  const [sizes, setSizes] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    setFile(selected);

    if (!selected) {
      setSizePlaceholder("800x600");
      return;
    }

    const img = new window.Image();
    const objectUrl = URL.createObjectURL(selected);
    img.onload = () => {
      setSizePlaceholder(`${img.naturalWidth}x${img.naturalHeight}`);
      URL.revokeObjectURL(objectUrl);
    };
    img.onerror = () => {
      setSizePlaceholder("800x600");
      URL.revokeObjectURL(objectUrl);
    };
    img.src = objectUrl;
  };

  // Redirect if not admin
  useEffect(() => {
    if (role !== "admin") navigate("/gallery");
  }, [role, navigate]);

const addSize = () => {
  // Check format
  if (!sizeInput.includes("x")) {
    setErrorMsg("Enter size in format: 800x600");
    return;
  }

  // Parse width and height
  const [wStr, hStr] = sizeInput.split("x");
  const w = parseInt(wStr.trim(), 10);
  const h = parseInt(hStr.trim(), 10);

  if (!w || !h || isNaN(w) || isNaN(h)) {
    setErrorMsg("Invalid size value");
    return;
  }

  // Check for duplicate
  const exists = sizes.some(s => s.width === w && s.height === h);
  if (exists) {
    setErrorMsg("Size already added"); // ✅ Show duplicate message
    return;
  }

  // Add size
  setSizes(prev => [...prev, { width: w, height: h }]);
  setSizeInput("");
  setErrorMsg(""); // Clear only if a new valid size is added
};


  const removeSize = index => setSizes(prev => prev.filter((_, i) => i !== index));

const uploadImage = async () => {
  if (!file) return setErrorMsg("Select an image to upload");
  if (sizes.length === 0) return setErrorMsg("Add at least one size before uploading");

  setLoading(true);
  setErrorMsg("");

  const formData = new FormData();
  formData.append("image", file);
  formData.append("sizes", JSON.stringify(sizes));

  try {
    const res = await api.post("/images/upload", formData, { 
      headers: { Authorization: `Bearer ${token}` } 
    });

    if (res.data.addedSizes === 0) {
      // Server can optionally send this
      setErrorMsg("No new sizes were added — duplicates detected");
      return;
    }

    // Reset form after successful upload
    setFile(null);
    setSizes([]);
    setSizeInput("");
    setSizePlaceholder("800x600");
    if (fileInputRef.current) fileInputRef.current.value = "";

    navigate("/gallery", { state: { successMsg: "Image uploaded and resized successfully ✅" } });
  } catch (err) {
    setErrorMsg(err.response?.data?.message || "Upload failed");
  } finally {
    setLoading(false);
  }
};

const handleLogout = () => {
  // Remove token from localStorage if you stored it there
  localStorage.removeItem("token");
  localStorage.removeItem("role");

  // Redirect to login
  navigate("/");
};


  return (
    <div className="admin-container">
      {/* HEADER */}
      <header className="admin-header">
        <h2 className="admin-title">Admin Upload</h2>
        <div className="admin-header-buttons">
          <button className="admin-btn" onClick={() => navigate("/link")}>Link Creation</button>
          <button className="admin-btn"onClick={() => navigate("/admin/pending-users")}>Users</button>
          <button className="admin-btn" onClick={() => navigate("/gallery")}>View Gallery</button>
          <button className="logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </header>

      {/* UPLOAD CARD */}
      <div className="admin-card">
        <div className="form-group">
          <label>Image File</label>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} />
        </div>

        <div className="form-group">
          <label>Add Size</label>
          <div className="size-row">
            <input type="text" placeholder={sizePlaceholder} value={sizeInput} onChange={e => setSizeInput(e.target.value)} />
            <button className="add-btn" onClick={addSize}>Add</button>
          </div>
        </div>

        <div className="size-tags">
          {sizes.map((s, i) => (
            <div key={i} className="tag">
              {s.width}×{s.height} <span onClick={() => removeSize(i)}>✖</span>
            </div>
          ))}
        </div>

        {errorMsg && <div className="error-msg">{errorMsg}</div>}

        <button className="upload-btn" onClick={uploadImage} disabled={loading}>
          {loading ? "Uploading..." : "Upload & Resize"}
        </button>
      </div>
    </div>
  );
}

export default AdminUpload;
